import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';

import { AgricolaSearchType } from '../../../interfaces/agricola.interface';
import { AgricolaService } from '../../../services/agricola.service';
import { AgricolaClientNotFoundDialogComponent } from './agricola-client-not-found-dialog/agricola-client-not-found-dialog.component';
import { agricolaPositiveIntegerValidator } from '../agricola-validators';

@Component({
  selector: 'app-agricola-client-search',
  templateUrl: './agricola-client-search.component.html',
  styleUrls: ['./agricola-client-search.component.css'],
  standalone: false
})
export class AgricolaClientSearchComponent implements OnInit {

  searchForm: FormGroup;
  selectedSearchType: string = 'numero-unico';
  isLoading = false;
  searchLoadedKey = 0;
  searchLoadFailed = false;

  searchTypes: AgricolaSearchType[] = [
    { value: 'numero-unico',    label: 'Número único',        placeholder: 'Buscar número único',        maxLength: 9,  numericOnly: true },
    { value: 'dui',             label: 'DUI',                  placeholder: 'Buscar DUI',                 maxLength: 9,  numericOnly: true },
    { value: 'nit',             label: 'NIT',                  placeholder: 'Buscar NIT',                 maxLength: 14, numericOnly: true },
    { value: 'carnet-residente',label: 'Carnet de residente',  placeholder: 'Buscar carnet de residente', maxLength: 9  },
    { value: 'pasaporte',       label: 'Pasaporte',            placeholder: 'Buscar pasaporte',           maxLength: 20 }
  ];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private agricolaService: AgricolaService,
    private dialog: MatDialog
  ) {
    this.searchForm = this.fb.group({
      searchType:  ['numero-unico', Validators.required],
      searchValue: ['', this.searchValueValidators(this.searchTypes[0])]
    });
  }

  ngOnInit(): void {
    this.agricolaService.initGenericInsurance();

    this.searchForm.get('searchType')?.valueChanges.subscribe((value: string) => {
      this.selectedSearchType = value;
      const searchType = this.searchTypes.find(st => st.value === value);
      if (searchType) {
        this.searchForm.get('searchValue')?.setValue('');
        this.searchForm.get('searchValue')?.setValidators(this.searchValueValidators(searchType));
        this.searchForm.get('searchValue')?.updateValueAndValidity();
      }
    });
  }

  private searchValueValidators(searchType: AgricolaSearchType): ValidatorFn[] {
    const validators = [Validators.required, Validators.maxLength(searchType.maxLength)];
    if (searchType.numericOnly) {
      validators.push(agricolaPositiveIntegerValidator());
    }
    return validators;
  }

  get isSearchValueCorrect(): boolean {
    const control = this.searchForm.get('searchValue');
    return !!control?.valid && !!control.value;
  }

  // Shown under the input so an invalid document stops the search before it
  // reaches the backend, instead of returning "cliente no encontrado".
  get searchValueError(): string {
    const control = this.searchForm.get('searchValue');
    if (!control?.hasError('invalidInteger') || !(control.touched || control.dirty)) {
      return '';
    }
    return `${this.getCurrentSearchType()?.label ?? 'Dato'} inválido: ingresa solo números.`;
  }

  getCurrentSearchType(): AgricolaSearchType | undefined {
    return this.searchTypes.find(st => st.value === this.selectedSearchType);
  }

  getCharacterCount(): string {
    const searchType = this.getCurrentSearchType();
    return searchType ? `máximo ${searchType.maxLength} caracteres` : '';
  }

  onSearch(): void {
    if (this.searchForm.invalid) {
      return;
    }

    this.isLoading = true;
    this.searchLoadFailed = false;

    const searchType  = this.searchForm.get('searchType')?.value;
    const searchValue = this.searchForm.get('searchValue')?.value;

    this.agricolaService.searchClient({ searchType, searchValue }).subscribe({
      next: () => {
        this.isLoading = false;
        this.searchLoadedKey++;
        this.router.navigate(['/agricola/form-client']);
      },
      error: () => {
        this.isLoading = false;
        this.searchLoadFailed = true;
        // searchClient siembra el criterio y el titular antes de consultar; si
        // la búsqueda falla no debe quedar nada de ella en la emisión.
        this.agricolaService.initGenericInsurance();
        this.dialog.open(AgricolaClientNotFoundDialogComponent, {
          width: '520px'
        });
      }
    });
  }
}
